import React, { useState, useEffect, isValidElement } from 'react';
import {
  Collapse,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
  Tabs
} from 'antd';
import {
  DrawerEdit,
  FormItemWrap,
  api,
  ResourceEdit,
  useCrudEditContext
} from '@unicus/react-ui-kit';
import {
  FormItemUserDetail,
  InputCurrency,
  InputPercent,
  RemoteSelectCrud,
  RemoteSelectV2,
  ResCol
} from 'components';
import isNullOrEmpty from 'util/functions/isNullOrEmpty';
import useCompany from 'core/hooks/useCompany';
import { useDispatch } from 'react-redux';
import useSegmentField from 'core/hooks/useSegmentField';
import { cancelEditInvoice } from 'stores/financialEntries/financialEntryActions';
import moment from 'moment';
import { round } from 'util/functions/decimal';
import { normalizeFromSelect, normalizeToSelect } from 'util/converters';
import RemoteSelect from 'components/RemoteSelect';
import InvoiceHelper from 'helpers';
import { MaskedInput } from 'antd-mask-input';
import { formatFederalTaxNumber } from 'util/formatters';
import CustomerEdit from '../customers/CustomerEdit';

const FinancialEntryInvoiceEdit = (props) => {
  const { data } = props;
  const dispatch = useDispatch();
  const segmentFields = useSegmentField();
  const [hasPerson] = useState(!!(data.patient ?? data.customer));
  const [currentPatient, setCurrentPatient] = useState(data.patient);
  const isNew = !data?.invoice?.id;
  const [tabKey, setTabKey] = useState('tab_patient');
  const { form } = useCrudEditContext();
  const { company } = useCompany();

  const isFlorianopolis = company?.city?.ibgeCode === 4205407;

  const handleBeforeSubmit = ({ invoice, patient, ...rest }) => {
    return {
      ...data,
      ...rest,
      patient: normalizeFromSelect(patient),
      invoice: {
        ...(data.invoice ?? {}),
        ...invoice,
        company: normalizeFromSelect(invoice?.company),
        effectiveDate: moment(invoice?.effectiveDate).format('YYYY-MM-DD HH:mm')
      }
    };
  };

  const handleBeforeBindingIInvoice = (invoice) => {
    const {
      authorizationDate,
      cancellationDate,
      creationTime,
      lastModificationTime
    } = invoice ?? {};

    return {
      ...invoice,
      authorizationDate: authorizationDate ? moment(authorizationDate) : null,
      cancellationDate: cancellationDate ? moment(cancellationDate) : null,
      company: normalizeToSelect(invoice?.company),
      effectiveDate: invoice?.effectiveDate
        ? moment(invoice?.effectiveDate)
        : moment(),
      creationTime: creationTime ? moment(creationTime) : null,
      lastModificationTime: lastModificationTime
        ? moment(lastModificationTime)
        : null
    };
  };

  const handleBeforeBinding = ({ invoice, patient, ...rest }) => {
    setCurrentPatient(patient);

    if (patient != null) {
      setTabKey('tab_patient');
    }

    if (rest?.customer != null) {
      setTabKey('tab_customer');
    }

    return {
      ...rest,
      patient: normalizeToSelect(patient),
      invoice: handleBeforeBindingIInvoice(invoice)
    };
  };

  const readOnly = InvoiceHelper.isReadOnly(data?.invoice?.status);

  const handleCancel = () => {
    dispatch(cancelEditInvoice());
  };

  const calculeAmount = (base, rate) => {
    return round(base * (rate / 100));
  };

  const calculateDisplayTaxes = (total = {}) => {
    const display = {};

    display.issAmount = total.issWithheld ? total.issAmount : 0;
    display.pisAmount = total.pisWithheld ? total.pisAmount : 0;
    display.cofinsAmount = total.cofinsWithheld ? total.cofinsAmount : 0;
    display.csllAmount = total.csllWithheld ? total.csllAmount : 0;
    display.irAmount = total.irWithheld ? total.irAmount : 0;
    display.inssAmount = total.inssWithheld ? total.inssAmount : 0;

    return display;
  };

  const recalcuteAll = (allFields) => {
    let { invoice = {} } = allFields;
    const { amount } = data;
    const { total = {} } = invoice;

    const {
      deductionsAmount = 0,
      issRate = 0,
      pisRate = 0,
      cofinsRate = 0,
      irRate = 0,
      csllRate = 0,
      inssRate = 0
    } = total;

    invoice = {
      ...invoice,
      total: {
        invoiceAmount: amount,
        issBaseTax: amount
      }
    };

    total.invoiceAmount = amount;
    total.issBaseTax =
      issRate > 0 ? total.invoiceAmount - deductionsAmount : 0.0;
    total.issAmount = calculeAmount(total.issBaseTax, issRate);
    total.pisAmount = calculeAmount(total.invoiceAmount, pisRate);
    total.cofinsAmount = calculeAmount(total.invoiceAmount, cofinsRate);
    total.csllAmount = calculeAmount(total.invoiceAmount, csllRate);
    total.irAmount = calculeAmount(total.invoiceAmount, irRate);
    total.inssAmount = calculeAmount(total.invoiceAmount, inssRate);

    const displayTaxes = calculateDisplayTaxes(total);

    form.setFieldsValue({
      ...allFields,
      invoice: { ...invoice, total },
      display: displayTaxes
    });
  };

  const handleLoadedSettings = (settings) => {
    const {
      additionalInformation,
      cityServiceCode,
      cnaeCode,
      federalServiceCode,
      taxationType,
      ...total
    } = settings;
    const { effectiveDate, amount } = data;

    const initialValues = {
      ...data,
      invoice: {
        discrimination: data.description,
        effectiveDate: effectiveDate ? moment(effectiveDate) : null,
        additionalInformation,
        cityServiceCode,
        cnaeCode,
        federalServiceCode,
        taxationType,
        settings,
        total: {
          ...total,
          invoiceAmount: amount
        }
      }
    };

    recalcuteAll(initialValues);
    form.setFieldsValue(initialValues);
  };

  const handleGetSettings = (settingsId) => {
    api.fetch(`companies/invoice-settings/${settingsId}`).then(({ data }) => {
      handleLoadedSettings(data?.result);
    });
  };

  const handleFieldsChange = ({ settings }, allFields) => {
    if (settings !== undefined) {
      handleGetSettings(settings?.id);
    }

    recalcuteAll(allFields);
  };

  const getTitle = () => {
    return readOnly ? 'Visualizar nota fiscal' : 'Editar nota fiscal';
  };

  const handleTabChange = (tab) => {
    const { patient, customer } = form.getFieldsValue();

    if (tab === 'tab_customer' && patient) {
      Modal.confirm({
        title: `Deseja remover o ${segmentFields.serviceRecipient.toLowerCase()}?`,
        onOk: () => {
          form.setFieldsValue({ patient: null });
          setTabKey(tab);
        }
      });

      return;
    }

    if (tab === 'tab_patient' && customer) {
      Modal.confirm({
        title: `Deseja remover o ${segmentFields.client.toLowerCase()}?`,
        onOk: () => {
          form.setFieldsValue({ customer: null });
          setTabKey(tab);
        }
      });

      return;
    }

    setTabKey(tab);
  };

  const handleSuccessUpdate = (response) => {
    handleCancel();
    if (props.onCreateOrUpdateSuccess) {
      props.onCreateOrUpdateSuccess(response);
    }
  };

  useEffect(() => {
    if (isNew || ['rejected', 'created'].includes(data.invoice?.status)) {
      api.fetch('invoices/settings-default').then(({ data }) => {
        handleLoadedSettings(data?.result);
      });
    }
  }, []);

  return (
    <DrawerEdit
      {...props}
      title={getTitle()}
      size="md"
      onSubmit={handleBeforeSubmit}
      onBeforeBinding={handleBeforeBinding}
      onValuesChange={handleFieldsChange}
      cancelEdit={handleCancel}
      onCreateOrUpdateSuccess={handleSuccessUpdate}
      readOnly={readOnly}
    >
      <Form layout="vertical" preserve>
        <>
          <Form.Item name={['invoice', 'cityServiceCode']} hidden />
          <Form.Item name={['invoice', 'cnaeCode']} hidden />
          <Form.Item name={['invoice', 'federalServiceCode']} hidden />
          <Form.Item name={['invoice', 'taxationType']} hidden />

          <Row>
            <ResCol span={24}>
              <FormItemWrap
                name={['invoice', 'settings']}
                label="Configurações"
                required
              >
                <RemoteSelectV2
                  resource="invoices/settings"
                  disabled={readOnly}
                  allowClear={false}
                />
              </FormItemWrap>
            </ResCol>
          </Row>

          <Row>
            <ResCol>
              <Tabs activeKey={tabKey} onChange={handleTabChange}>
                <Tabs.TabPane
                  key="tab_patient"
                  tab={segmentFields.serviceRecipient}
                  tabKey="tab_patient"
                  disabled={readOnly || hasPerson}
                >
                  <FormItemWrap
                    name="patient"
                    label={segmentFields.serviceRecipient}
                  >
                    <RemoteSelect
                      resource="patients"
                      disabled={readOnly || hasPerson}
                      optionRender={(c) => (
                        <Select.Option
                          key={c.key}
                          value={c.key}
                          label={c.label}
                        >
                          <div label={c.label}>
                            <div>{c.item.name}</div>
                            {c.item.federalTaxNumber ? (
                              <div className="gx-fs-sm">
                                {formatFederalTaxNumber(
                                  c.item.federalTaxNumber
                                )}
                              </div>
                            ) : null}
                          </div>
                        </Select.Option>
                      )}
                    />
                  </FormItemWrap>
                </Tabs.TabPane>
                <Tabs.TabPane
                  key="tab_customer"
                  tab={segmentFields.client}
                  tabKey="tab_customer"
                  disabled={readOnly || hasPerson}
                >
                  <FormItemWrap name="customer" label={segmentFields.client}>
                    <RemoteSelectCrud
                      resource="customers"
                      textPropName="name"
                      resourceTitle={segmentFields.client}
                      editComponent={CustomerEdit}
                      disabled={readOnly || hasPerson}
                      optionRender={(c) => (
                        <Select.Option
                          key={c.id}
                          value={c.id}
                          label={c.name}
                          item={c}
                        >
                          <div label={c.name}>
                            <div>{c.name}</div>
                            {c.federalTaxNumber ? (
                              <div className="gx-fs-sm">
                                {formatFederalTaxNumber(c.federalTaxNumber)}
                              </div>
                            ) : null}
                          </div>
                        </Select.Option>
                      )}
                    />
                  </FormItemWrap>
                </Tabs.TabPane>
              </Tabs>
            </ResCol>
          </Row>

          {currentPatient?.responsible && (
            <Row>
              <ResCol span={12}>
                <Form.Item
                  name={['patient', 'item', 'responsible', 'federalTaxNumber']}
                  label="CPF do responsável"
                >
                  <MaskedInput mask="111.111.111-11" disabled />
                </Form.Item>
              </ResCol>
              <ResCol span={12}>
                <FormItemWrap
                  name={['patient', 'item', 'responsible', 'name']}
                  label="Nome do responsável"
                >
                  <Input disabled />
                </FormItemWrap>
              </ResCol>
            </Row>
          )}

          <Row>
            <ResCol span={24}>
              <FormItemWrap
                name={['invoice', 'discrimination']}
                label="Discriminação"
              >
                <Input.TextArea rows={2} maxLength={2000} disabled={readOnly} />
              </FormItemWrap>
            </ResCol>
          </Row>
          <Row gutter={[10, 0]}>
            <ResCol span={5}>
              <FormItemWrap
                name={['invoice', 'effectiveDate']}
                label="Competência"
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  disabled={readOnly}
                  disabledDate={(date) => date.isAfter(moment(), 'day')}
                />
              </FormItemWrap>
            </ResCol>
            <ResCol span={5}>
              <FormItemWrap
                className="ant-form-item-money"
                name={['invoice', 'total', 'invoiceAmount']}
                label="Valor total"
              >
                <InputCurrency disabled />
              </FormItemWrap>
            </ResCol>
            <ResCol span={5}>
              <FormItemWrap
                className="ant-form-item-money"
                name={['invoice', 'total', 'deductionsAmount']}
                label="Deduções"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (
                        !value ||
                        getFieldValue(['invoice', 'total', 'invoiceAmount']) >=
                          value
                      ) {
                        return Promise.resolve();
                      }

                      return Promise.reject(new Error('Valor inválido'));
                    }
                  })
                ]}
              >
                <InputCurrency disabled={readOnly} />
              </FormItemWrap>
            </ResCol>
            <ResCol span={4}>
              <FormItemWrap
                name={['invoice', 'total', 'issBaseTax']}
                label="Base cálculo"
              >
                <InputCurrency disabled />
              </FormItemWrap>
            </ResCol>

            <ResCol span={5}>
              <FormItemWrap
                className="ant-form-item-money"
                name={['invoice', 'total', 'netAmount']}
                label="Valor líquido"
              >
                <InputCurrency disabled />
              </FormItemWrap>
            </ResCol>
          </Row>

          <Row>
            {data.invoice?.status === 'authorized' && (
              <ResCol span={8}>
                <FormItemWrap
                  name={['invoice', 'authorizationDate']}
                  label="Data de autorização"
                >
                  <DatePicker disabled format="DD/MM/YYYY" />
                </FormItemWrap>
              </ResCol>
            )}
            {data.invoice?.status === 'canceled' && (
              <ResCol span={8}>
                <FormItemWrap
                  name={['invoice', 'cancellationDate']}
                  label="Data de cancelamento"
                >
                  <DatePicker disabled format="DD/MM/YYYY" />
                </FormItemWrap>
              </ResCol>
            )}
          </Row>
          {!isNullOrEmpty(data.id) &&
            (data.invoice?.rpsNumber > 0 || data.invoice?.number > 0) && (
              <Row>
                <ResCol span={isFlorianopolis ? 24 : 7}>
                  <FormItemWrap name={['invoice', 'number']} label="Número NF">
                    <Input disabled />
                  </FormItemWrap>
                </ResCol>
                {!isFlorianopolis && (
                  <>
                    <ResCol span={6}>
                      <FormItemWrap
                        name={['invoice', 'rpsNumber']}
                        label="Número do RPS"
                      >
                        <InputNumber min={0} max={99999999999} disabled />
                      </FormItemWrap>
                    </ResCol>
                    <ResCol span={5}>
                      <FormItemWrap
                        name={['invoice', 'rpsSeries']}
                        label="Série do RPS"
                      >
                        <Input maxLength={10} disabled />
                      </FormItemWrap>
                    </ResCol>
                  </>
                )}
              </Row>
            )}

          <Collapse>
            <Collapse.Panel
              forceRender
              header="Impostos"
              key="1"
              className="gx-p-0"
            >
              <Row gutter={[10, 0]}>
                <ResCol span={3}>
                  <FormItemWrap
                    name={['invoice', 'total', 'issRate']}
                    label="ISS"
                  >
                    <InputPercent disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    className="ant-form-item-money"
                    name={['invoice', 'total', 'issAmount']}
                    label="Valor ISS"
                  >
                    <InputCurrency disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    name={['invoice', 'total', 'pisRate']}
                    label="PIS"
                  >
                    <InputPercent disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    className="ant-form-item-money"
                    name={['invoice', 'total', 'pisAmount']}
                    label="Valor PIS"
                  >
                    <InputCurrency disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    name={['invoice', 'total', 'cofinsRate']}
                    label="COFINS"
                  >
                    <InputPercent disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={5}>
                  <FormItemWrap
                    className="ant-form-item-money"
                    name={['invoice', 'total', 'cofinsAmount']}
                    label="Valor COFINS"
                  >
                    <InputCurrency disabled />
                  </FormItemWrap>
                </ResCol>
              </Row>
              <Row gutter={[10, 0]}>
                <ResCol span={3}>
                  <FormItemWrap
                    name={['invoice', 'total', 'csllRate']}
                    label="CSLL"
                  >
                    <InputPercent disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    className="ant-form-item-money"
                    name={['invoice', 'total', 'csllAmount']}
                    label="Valor CSLL"
                  >
                    <InputCurrency disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    name={['invoice', 'total', 'irRate']}
                    label="IR"
                  >
                    <InputPercent disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    className="ant-form-item-money"
                    name={['invoice', 'total', 'irAmount']}
                    label="Valor IR"
                  >
                    <InputCurrency disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    name={['invoice', 'total', 'inssRate']}
                    label="INSS"
                  >
                    <InputPercent disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={5}>
                  <FormItemWrap
                    className="ant-form-item-money"
                    name={['invoice', 'total', 'inssAmount']}
                    label="Valor INSS"
                  >
                    <InputCurrency disabled />
                  </FormItemWrap>
                </ResCol>
              </Row>
              <Divider orientation="left">Retenções</Divider>
              <Row gutter={[1, 0]}>
                <ResCol span={4}>
                  <FormItemWrap
                    name={['invoice', 'total', 'issWithheld']}
                    label="ISS"
                    valuePropName="checked"
                  >
                    <Switch disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    name={['invoice', 'total', 'pisWithheld']}
                    label="PIS"
                    valuePropName="checked"
                  >
                    <Switch disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    name={['invoice', 'total', 'cofinsWithheld']}
                    label="COFINS"
                    valuePropName="checked"
                  >
                    <Switch disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    name={['invoice', 'total', 'csllWithheld']}
                    label="CSLL"
                    valuePropName="checked"
                  >
                    <Switch disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    name={['invoice', 'total', 'irWithheld']}
                    label="IR"
                    valuePropName="checked"
                  >
                    <Switch disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    name={['invoice', 'total', 'inssWithheld']}
                    labelAlign="center"
                    label="INSS"
                    valuePropName="checked"
                  >
                    <Switch disabled />
                  </FormItemWrap>
                </ResCol>
              </Row>
              <Row gutter={[10, 0]}>
                <ResCol span={4}>
                  <FormItemWrap
                    className="ant-form-item-money"
                    name={['display', 'issAmount']}
                    label="Valor retido"
                  >
                    <InputCurrency disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    className="ant-form-item-money"
                    name={['display', 'pisAmount']}
                    label="Valor retido"
                  >
                    <InputCurrency disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    className="ant-form-item-money"
                    name={['display', 'cofinsAmount']}
                    label="Valor retido"
                  >
                    <InputCurrency disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    className="ant-form-item-money"
                    name={['display', 'csllAmount']}
                    label="Valor retido"
                  >
                    <InputCurrency disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    className="ant-form-item-money"
                    name={['display', 'irAmount']}
                    label="Valor retido"
                  >
                    <InputCurrency disabled />
                  </FormItemWrap>
                </ResCol>
                <ResCol span={4}>
                  <FormItemWrap
                    className="ant-form-item-money"
                    name={['display', 'inssAmount']}
                    label="Valor retido"
                  >
                    <InputCurrency disabled />
                  </FormItemWrap>
                </ResCol>
              </Row>
            </Collapse.Panel>
          </Collapse>

          {readOnly && (
            <>
              <Divider orientation="left">Auditoria</Divider>
              <Row>
                <ResCol span={12}>
                  <FormItemWrap
                    name={['invoice', 'creationTime']}
                    label="Data de lançamento"
                  >
                    <DatePicker format="DD/MM/YYYY HH:mm" disabled />
                  </FormItemWrap>
                </ResCol>

                <ResCol span={12}>
                  <FormItemWrap
                    name={['invoice', 'lastModificationTime']}
                    label="Data da última alteração"
                    hidden={!data.userLastModifier}
                  >
                    <DatePicker format="DD/MM/YYYY HH:mm" disabled />
                  </FormItemWrap>
                </ResCol>
              </Row>
              <Row>
                <ResCol span={12}>
                  <FormItemWrap
                    name={['invoice', 'userCreator']}
                    label="Usuário de lançamento"
                  >
                    <FormItemUserDetail />
                  </FormItemWrap>
                </ResCol>

                <ResCol span={12}>
                  <FormItemWrap
                    name={['invoice', 'userLastModifier']}
                    label="Usuário da última alteração"
                    hidden={!data.userLastModifier}
                  >
                    <FormItemUserDetail />
                  </FormItemWrap>
                </ResCol>
              </Row>
            </>
          )}
        </>
      </Form>
    </DrawerEdit>
  );
};

export default ResourceEdit.create(FinancialEntryInvoiceEdit, {
  resource: 'financial-entries'
});
